import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Radar, Map, LineChart, ArrowRight } from "lucide-react";

const FEATURES = [
  { icon: Radar, title: "Lead intelligenti", text: "Traccia ogni trattativa per fonte, stato e priorità." },
  { icon: LineChart, title: "Analisi & margini", text: "Fatturato, sconti e margini per cliente, prodotto e zona." },
  { icon: Map, title: "Clienti sulla mappa", text: "Ottimizza i giri visita con percorsi e appuntamenti." },
];

export default function Login() {
  const { login, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // 2FA
  const [challenge, setChallenge] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otp, setOtp] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data?.twofa_required) {
        setChallenge(data.challenge);
        setMaskedEmail(data.email || "");
        setOtp("");
      } else {
        navigate("/");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyOtp(challenge, otp);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const doResend = async () => {
    setError("");
    try {
      await resendOtp(challenge);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_0.95fr]">
      {/* Brand panel */}
      <div
        className="relative hidden lg:flex flex-col justify-between p-14 overflow-hidden text-white"
        style={{ backgroundColor: "#0a0f1f" }}
      >
        {/* glows + grid */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background:
            "radial-gradient(600px circle at 15% 20%, rgba(37,99,235,0.28), transparent 45%), radial-gradient(500px circle at 85% 85%, rgba(6,182,212,0.20), transparent 45%)",
        }} />
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
          backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }} />

        <div className="relative z-10 flex items-center gap-3">
          <img
            src="/sentrion-logo.png"
            alt="Sentrion Plus"
            className="w-12 h-12 rounded-xl shadow-lg ring-1 ring-white/10 object-cover"
          />
          <div className="leading-tight">
            <span className="font-heading font-bold text-xl tracking-tight block">Sentrion Plus</span>
            <span className="text-[11px] text-white/60 uppercase tracking-[0.22em]">
              Sales · Intelligence · CRM
            </span>
          </div>
        </div>

        <div className="relative z-10 space-y-8 max-w-lg">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-white/70 backdrop-blur">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#22c55e" }} />
              La tua centrale vendite
            </div>
            <h1 className="font-heading text-4xl xl:text-5xl font-extrabold tracking-tighter leading-[1.05]">
              Ogni trattativa,<br />sotto controllo.
            </h1>
          </div>
          <div className="space-y-3">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-3.5 backdrop-blur-sm">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(59,130,246,0.18)" }}>
                    <Icon className="w-4.5 h-4.5" style={{ color: "#60a5fa", width: 18, height: 18 }} />
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{f.title}</div>
                    <div className="text-sm text-white/60 leading-snug">{f.text}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-2 text-white/50 text-xs">
          {["Fredda", "Tiepida", "Calda", "Chiusa"].map((s, i) => (
            <span key={s} className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-full border border-white/15 px-2.5 py-1 font-medium text-white/70">{s}</span>
              {i < 3 && <ArrowRight className="w-3 h-3" />}
            </span>
          ))}
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <form onSubmit={challenge ? submitOtp : submit} className="w-full max-w-sm space-y-7" data-testid="login-form">
          {/* mobile brand */}
          <div className="flex lg:hidden items-center gap-3 mb-2">
            <img src="/sentrion-logo.png" alt="Sentrion Plus" className="w-11 h-11 rounded-xl ring-1 ring-border object-cover" />
            <div className="leading-tight">
              <span className="font-heading font-bold text-lg tracking-tight block">Sentrion Plus</span>
              <span className="text-[10px] text-muted-foreground uppercase tracking-[0.2em]">Sales · Intelligence · CRM</span>
            </div>
          </div>

          <div className="space-y-1.5">
            <h2 className="font-heading text-3xl font-extrabold tracking-tighter">
              {challenge ? "Verifica in due passaggi" : "Bentornato"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {challenge
                ? `Abbiamo inviato un codice a ${maskedEmail}. Inseriscilo per accedere.`
                : "Accedi alla tua centrale vendite Sentrion Plus."}
            </p>
          </div>

          {error && (
            <div
              className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          {challenge ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="otp">Codice di verifica</Label>
                <Input
                  id="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="6 cifre"
                  required
                  className="h-11 tracking-[0.4em] text-center text-lg"
                  data-testid="otp-input"
                />
              </div>
              <Button type="submit" className="w-full h-11 gap-2 text-base" disabled={loading || otp.length < 6} data-testid="otp-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Verifica <ArrowRight className="w-4 h-4" /></>)}
              </Button>
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={doResend} className="text-primary hover:underline" data-testid="otp-resend">
                  Rinvia codice
                </button>
                <button type="button" onClick={() => { setChallenge(""); setOtp(""); setError(""); }} className="text-muted-foreground hover:underline" data-testid="otp-back">
                  Torna al login
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@azienda.it"
                  required
                  className="h-11"
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-link">
                    Password dimenticata?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-11"
                  data-testid="login-password"
                />
              </div>

              <Button type="submit" className="w-full h-11 gap-2 text-base" disabled={loading} data-testid="login-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Accedi <ArrowRight className="w-4 h-4" /></>)}
              </Button>
            </>
          )}

          <p className="text-center text-xs text-muted-foreground">
            © {new Date().getFullYear()} Sentrion Plus — Sales, Intelligence &amp; CRM
          </p>
        </form>
      </div>
    </div>
  );
}
